interface DependencyMemo<T = unknown> {
    get: (compute: () => T, deps: unknown[]) => T;
}

/**
 * Checks if two arrays of dependencies are equal (reference equality per element).
 */
function depsEqual(a: unknown[], b: unknown[]): boolean {
    return a.length === b.length && a.every((d, i) => d === b[i]);
}

/**
 * Creates a dependency memo that caches the result of a computation until dependencies change.
 * One instance per memoized value.
 *
 * @example
 *   private memo = createDependencyMemo<T>();
 *   const value = this.memo.get(
 *     () => { ... },
 *     [dependency1, dependency2, ...]
 *   );
 */
export function createDependencyMemo<T = unknown>(): DependencyMemo<T> {
    let lastDeps: unknown[] | undefined;
    let lastValue: T | undefined;
    return {
        get: (compute: () => T, deps: unknown[]): T => {
            if (lastDeps === undefined || !depsEqual(lastDeps, deps)) {
                lastDeps = [...deps];
                lastValue = compute();
            }

            return lastValue as T;
        },
    };
}
